import { hasFeature } from "../features.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  civilInstant,
  EXPENSE_STATUSES,
  expenseTotals,
  gstOf,
  localDateIn,
  monthOf,
  timezoneOf,
  type DextDelivery,
  type ExpenseStatus,
} from "@baros/core";
import { ApiError } from "../api.js";
import { useApp } from "../context.js";
import { money, venueZone } from "../format.js";
import type { ScreenProps } from "../screens.js";
import { expensesApi, slipsApi, type Expense, type ExpenseDraft, type OrderCandidate, type SlipReading } from "./api.js";
import "./expenses.css";

const STATUS_LABEL: Readonly<Record<ExpenseStatus, string>> = {
  unmatched: "Awaiting slip",
  matched: "Matched",
  paid: "Paid",
};

const STATUS_COLOUR: Readonly<Record<ExpenseStatus, string>> = {
  unmatched: "var(--warn)",
  matched: "var(--tx)",
  paid: "var(--pos)",
};

/** What moving an expense along means, in the order it happens. */
const NEXT_STATUS: Readonly<Record<ExpenseStatus, ExpenseStatus | null>> = {
  unmatched: "matched",
  matched: "paid",
  paid: null,
};

const ALL = "all";

/**
 * Which month an invoice belongs to, on the venue's calendar.
 *
 * `iso.slice(0, 7)` is the UTC month, and Melbourne is ten or eleven hours
 * ahead of it: an invoice keyed in on the evening of the first is stored with
 * a timestamp that still says the month before, so September's total kept
 * October's first night in it and the month picker flipped over at ten in the
 * morning. See issue #60.
 */
const monthKey = (iso: string, tz: string) => monthOf(new Date(iso), tz);

const monthLabel = (key: string) => {
  const [year, month] = key.split("-");
  const d = new Date(Number(year), Number(month) - 1, 1);
  return d.toLocaleDateString("en-AU", { timeZone: venueZone(), month: "long", year: "numeric" });
};

const shortDay = (iso: string, timeZone: string) =>
  new Date(iso).toLocaleDateString("en-AU", { timeZone, day: "2-digit", month: "short" });

/** Where a slip has got to with Dext, in a line. */
function dextLine(delivery: DextDelivery | null | undefined, timeZone: string): string {
  if (!delivery) return "Dext · not sent";
  const at = new Date(delivery.at);
  const when = Number.isNaN(at.getTime())
    ? ""
    : ` ${at.toLocaleString("en-AU", { timeZone, day: "2-digit", month: "short", hour: "numeric", minute: "2-digit" })}`;
  return delivery.status === "sent"
    ? `Dext · sent${when}`
    : `Dext · failed${when}${delivery.error ? ` — ${delivery.error}` : ""}`;
}

interface FormState {
  readonly expenseId: string | null;
  readonly ref: string;
  readonly supplierName: string;
  readonly amount: string;
  /** Empty means "let the server take an eleventh". */
  readonly gst: string;
  readonly status: ExpenseStatus;
  readonly note: string;
  readonly date: string;
  /** The uploaded slip, once there is one. */
  readonly slipS3Key: string | null;
  /** What Textract read, shown beside the fields it filled. */
  readonly reading: SlipReading | null;
  /** Orders the slip might pay for, best first. */
  readonly matches: readonly OrderCandidate[];
  /** The order chosen to link on save. */
  readonly orderId: string | null;
  readonly linkedSupplierId: string | null;
}

const blankForm = (tz: string): FormState => ({
  expenseId: null,
  ref: "",
  supplierName: "",
  amount: "",
  gst: "",
  status: "unmatched",
  note: "",
  // Today where the venue is, not where UTC is: an invoice entered on a
  // Friday evening in Melbourne must not default to Thursday's date.
  date: localDateIn(new Date(), tz),
  slipS3Key: null,
  reading: null,
  matches: [],
  orderId: null,
  linkedSupplierId: null,
});

/**
 * Screen 11. Every order that goes out lands here: reference, supplier,
 * amount, GST and where it has got to, with the period's totals.
 *
 * GST defaults to an eleventh of the inclusive amount (`gstOf`) and stays
 * editable, because fresh produce is GST-free and the slip is the authority.
 * A slip photo goes straight to the attachments bucket on a presigned URL and
 * Textract reads it into the form; the person checks every figure before saving.
 */
export function ExpensesScreen(_props: ScreenProps) {
  const app = useApp();
  const [expenses, setExpenses] = useState<readonly Expense[] | null>(null);
  const [monthIndex, setMonthIndex] = useState(0);
  const [status, setStatus] = useState<ExpenseStatus | typeof ALL>(ALL);
  const [form, setForm] = useState<FormState | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  const venueId = app.venueId;
  const canManage = app.me.can["expense:manage"];
  /** The venue's zone, off its META — the month is bounded on its calendar. */
  const tz = timezoneOf(app.venueState?.venue);

  const staffNames = useMemo(
    () => new Map((app.venueState?.staff ?? []).map((s) => [s.userId, s.displayName])),
    [app.venueState],
  );

  const load = useCallback(async () => {
    if (!venueId) return;
    try {
      const res = await expensesApi.list(app.token, venueId);
      setExpenses(res.expenses);
    } catch (err) {
      setMsg({ kind: "err", text: err instanceof Error ? err.message : "Could not load expenses" });
      setExpenses([]);
    }
  }, [app.token, venueId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Months with something in them, newest first, with this month at the front
  // whether or not anything has been entered yet.
  const months = useMemo(() => {
    const found = new Set<string>([monthOf(new Date(), tz)]);
    for (const e of expenses ?? []) found.add(monthKey(e.at, tz));
    return [...found].sort((a, b) => b.localeCompare(a));
  }, [expenses, tz]);

  // Open on the most recent month that has anything, so the screen is not
  // empty on the first of the month or on a fresh seed.
  useEffect(() => {
    if (!expenses || expenses.length === 0) return;
    const idx = months.indexOf(monthKey(expenses[0]!.at, tz));
    if (idx > 0) setMonthIndex(idx);
  }, [expenses, months, tz]);

  const shownMonth = months[Math.min(monthIndex, months.length - 1)] ?? months[0] ?? "";
  const inMonth = useMemo(
    () => (expenses ?? []).filter((e) => monthKey(e.at, tz) === shownMonth),
    [expenses, shownMonth, tz],
  );
  const rows = useMemo(
    () => inMonth.filter((e) => status === ALL || e.status === status),
    [inMonth, status],
  );

  // The month's totals, not the filtered view's — "spent in September" must
  // not change when you click a status chip.
  const totals = useMemo(() => expenseTotals(inMonth), [inMonth]);
  const counts = useMemo(() => {
    const c: Record<string, number> = { [ALL]: inMonth.length };
    for (const s of EXPENSE_STATUSES) c[s] = inMonth.filter((e) => e.status === s).length;
    return c;
  }, [inMonth]);

  function edit(e: Expense) {
    setMsg(null);
    setForm({
      expenseId: e.expenseId,
      ref: e.ref,
      supplierName: e.supplierName,
      amount: e.amountIncGst.toFixed(2),
      gst: e.gst.toFixed(2),
      status: e.status,
      note: e.note,
      date: localDateIn(new Date(e.at), tz),
      slipS3Key: e.slipS3Key,
      reading: null,
      matches: [],
      orderId: e.orderId ?? null,
      linkedSupplierId: e.supplierId,
    });
  }

  async function save() {
    if (!form || !venueId) return;
    const amountIncGst = Number.parseFloat(form.amount);
    if (!Number.isFinite(amountIncGst)) {
      setMsg({ kind: "err", text: "Enter the amount from the invoice" });
      return;
    }
    const typedGst = form.gst.trim() === "" ? undefined : Number.parseFloat(form.gst);
    const draft: ExpenseDraft = {
      ref: form.ref,
      // The supplier comes from the order the slip was matched to, if any.
      supplierId: form.linkedSupplierId,
      supplierName: form.supplierName,
      amountIncGst,
      ...(typedGst !== undefined && Number.isFinite(typedGst) ? { gst: typedGst } : {}),
      status: form.status,
      note: form.note,
      // A date picked on a form is a civil date; the table stores instants.
      // Midday in the venue's zone is the one instant that reads back as the
      // day chosen under every rule applied to it — the calendar month the
      // list groups by, and the service date the dashboard's week filters on
      // — where UTC midnight lands on the day before in any zone west of it.
      at: civilInstant(form.date, 12, 0, tz).toISOString(),
      slipS3Key: form.slipS3Key,
    };

    setBusy(true);
    setMsg(null);
    try {
      let { expense } = form.expenseId
        ? await expensesApi.update(app.token, venueId, form.expenseId, draft)
        : await expensesApi.create(app.token, venueId, draft);
      if (form.orderId && expense.orderId !== form.orderId) {
        expense = (await slipsApi.link(app.token, venueId, expense.expenseId, form.orderId)).expense;
      }
      setExpenses((current) => {
        const rest = (current ?? []).filter((e) => e.expenseId !== expense.expenseId);
        return [expense, ...rest].sort((a, b) => b.at.localeCompare(a.at));
      });
      setForm(null);
      setMsg({ kind: "ok", text: `${expense.ref} · ${money(expense.amountIncGst)} saved` });
    } catch (err) {
      setMsg({
        kind: "err",
        text: err instanceof ApiError ? err.message : "Could not save that expense",
      });
    } finally {
      setBusy(false);
    }
  }

  const [slipBusy, setSlipBusy] = useState<string | null>(null);

  /**
   * Whether this organisation sends slips to Dext. Asked once; if the answer
   * is no, or there is no answer, the screen is the one it was before Dext —
   * no button, no state, no message.
   */
  const [dextOn, setDextOn] = useState(false);
  /** The expense whose slip is on its way. */
  const [dextBusy, setDextBusy] = useState<string | null>(null);

  useEffect(() => {
    // Dext switched off for the organisation (D14): not asked, not shown.
    if (!venueId || !hasFeature(app.me, "dext")) return;
    let live = true;
    expensesApi
      .dextConfig(app.token, venueId)
      .then((res) => {
        if (live) setDextOn(res?.configured === true);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [app.token, venueId]);

  /**
   * Email one slip to Dext. A second send of the same slip is asked about
   * first and goes with `force`; Dext drops an identical file as a duplicate,
   * so the worst a needless one does is nothing.
   */
  async function sendToDext(e: Expense) {
    if (!venueId) return;
    const again = e.dext?.status === "sent";
    if (
      again &&
      !window.confirm(
        `The slip for ${e.ref || e.supplierName} has already gone to Dext. Send it again?`,
      )
    ) {
      return;
    }
    setDextBusy(e.expenseId);
    setMsg(null);
    try {
      const { expense } = await expensesApi.sendToDext(app.token, venueId, e.expenseId, again);
      setExpenses((current) =>
        (current ?? []).map((x) => (x.expenseId === expense.expenseId ? expense : x)),
      );
      setMsg(
        expense.dext?.status === "failed"
          ? { kind: "err", text: expense.dext.error || "Dext did not take that slip" }
          : { kind: "ok", text: `Slip for ${expense.ref || expense.supplierName} sent to Dext` },
      );
    } catch (err) {
      // A failed send is recorded on the expense, so re-read the list for it
      // before saying what went wrong — in the API's own words. Only when the
      // API answered, though: `load` clears the list when it cannot reach the
      // server, and a dropped connection should not empty the screen.
      if (err instanceof ApiError) await load();
      setMsg({ kind: "err", text: err instanceof Error ? err.message : "Could not send that slip to Dext" });
    } finally {
      setDextBusy(null);
    }
  }

  /** Upload a photo of the slip, read it, and fill what it read into the form. */
  async function attachSlip(file: File) {
    if (!form || !venueId) return;
    setMsg(null);
    setSlipBusy("Uploading the slip…");
    try {
      const { key, url } = await slipsApi.presign(app.token, venueId, file.type);
      await slipsApi.upload(url, file);
      // Without invoice scanning (D14) the photo is attached and not read.
      if (!hasFeature(app.me, "slipScan")) {
        setForm((f) => (f ? { ...f, slipS3Key: key } : f));
        return;
      }
      setSlipBusy("Reading the slip…");
      const { reading, matches } = await slipsApi.read(app.token, venueId, key);
      setForm((f) =>
        f
          ? {
              ...f,
              slipS3Key: key,
              reading,
              matches,
              // A confident single match is pre-selected; anything less is the person's call.
              orderId: f.orderId ?? (matches[0] && matches[0].score >= 70 ? matches[0].orderId : null),
              linkedSupplierId: f.linkedSupplierId ?? (matches[0] && matches[0].score >= 70 ? matches[0].supplierId : null),
              supplierName: f.supplierName || reading.supplierName || "",
              ref: f.ref || reading.ref || "",
              date: reading.date ?? f.date,
              amount: f.amount || (reading.total !== null ? reading.total.toFixed(2) : ""),
              gst: f.gst || (reading.gst !== null ? reading.gst.toFixed(2) : ""),
              status: f.status === "unmatched" ? "matched" : f.status,
            }
          : f,
      );
    } catch (err) {
      setMsg({ kind: "err", text: err instanceof Error ? err.message : "Could not read the slip" });
    } finally {
      setSlipBusy(null);
    }
  }

  async function viewSlip(key: string) {
    if (!venueId) return;
    try {
      const { url } = await slipsApi.viewUrl(app.token, venueId, key);
      window.open(url, "_blank", "noopener");
    } catch (err) {
      setMsg({ kind: "err", text: err instanceof Error ? err.message : "Could not open the slip" });
    }
  }

  async function advance(e: Expense) {
    const next = NEXT_STATUS[e.status];
    if (!next || !venueId) return;
    try {
      const { expense } = await expensesApi.setStatus(app.token, venueId, e.expenseId, next);
      setExpenses((current) =>
        (current ?? []).map((x) => (x.expenseId === expense.expenseId ? expense : x)),
      );
    } catch (err) {
      setMsg({ kind: "err", text: err instanceof Error ? err.message : "Could not change status" });
    }
  }

  if (!venueId) {
    return (
      <div className="pad">
        <div className="msg err">You are not assigned to a venue, so there are no expenses to show.</div>
      </div>
    );
  }

  const suggestedGst = form ? gstOf(Number.parseFloat(form.amount) || 0) : 0;

  return (
    <div className="screen">
      <div className="row screen-head" style={{ padding: "18px 20px 12px", alignItems: "flex-end", gap: 14 }}>
        <div>
          <div className="label">{shownMonth ? monthLabel(shownMonth) : "—"}</div>
          <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>
            Every order lands here the moment it goes out.
          </div>
        </div>
        <span className="spacer" />
        <button
          className="btn"
          disabled={monthIndex >= months.length - 1}
          onClick={() => setMonthIndex((i) => Math.min(months.length - 1, i + 1))}
        >
          ← Earlier
        </button>
        <button className="btn" disabled={monthIndex <= 0} onClick={() => setMonthIndex((i) => Math.max(0, i - 1))}>
          Later →
        </button>
        {canManage && (
          <button className="btn dashed" style={{ height: 48 }} onClick={() => setForm(blankForm(tz))}>
            + Add expense
          </button>
        )}
      </div>

      {msg && (
        <div style={{ padding: "0 20px 10px" }}>
          <div className={`msg ${msg.kind === "err" ? "err" : "ok"}`}>{msg.text}</div>
        </div>
      )}

      <div className="row" style={{ padding: "0 20px 12px", flexWrap: "wrap" }}>
        {[ALL, ...EXPENSE_STATUSES].map((s) => (
          <button
            key={s}
            className="btn"
            aria-pressed={status === s}
            onClick={() => setStatus(s as ExpenseStatus | typeof ALL)}
          >
            <span
              className="dot"
              style={{ background: s === ALL ? "transparent" : STATUS_COLOUR[s as ExpenseStatus] }}
            />
            {s === ALL ? "All" : STATUS_LABEL[s as ExpenseStatus]}
            <span className="num muted" style={{ fontSize: 11, letterSpacing: 0 }}>{counts[s]}</span>
          </button>
        ))}
      </div>

      <div className="split">
        <div className="pane">
          <div className="scroll" style={{ padding: "0 20px 20px" }}>
            <div className="table">
              <div className="thead">
                <span className="e-c-date">Date</span>
                <span className="c-name">Supplier</span>
                <span className="e-c-ref">Reference</span>
                <span className="e-c-gst">GST</span>
                <span className="e-c-amt">Total</span>
                <span className="e-c-status">Status</span>
                {canManage && <span className="e-c-act">Actions</span>}
              </div>
              {rows.map((e) => (
                <div key={e.expenseId} className="trow">
                  <span className="e-c-date">{shortDay(e.at, tz)}</span>
                  <span className="c-name">
                    <span className="n">{e.supplierName}</span>
                    <span className="s">
                      <span className="e-slip" aria-hidden="true" />
                      <span>
                        {e.slipS3Key ? (
                          <button className="e-link" onClick={() => void viewSlip(e.slipS3Key!)}>View slip</button>
                        ) : "No slip"}{e.orderId ? " · order " + e.orderId : ""} · raised by{" "}
                        {staffNames.get(e.byUserId) ?? "—"}
                      </span>
                    </span>
                    {dextOn && e.slipS3Key && (
                      <span className="e-dext">
                        <span className={e.dext?.status === "failed" ? "neg" : undefined}>
                          {dextLine(e.dext, tz)}
                        </span>
                        {canManage && (
                          <button
                            className="btn sm"
                            disabled={dextBusy !== null}
                            onClick={() => void sendToDext(e)}
                          >
                            {dextBusy === e.expenseId
                              ? "Sending…"
                              : e.dext?.status === "sent"
                                ? "Send again"
                                : "Send to Dext"}
                          </button>
                        )}
                      </span>
                    )}
                  </span>
                  <span className="e-c-ref">{e.ref}</span>
                  <span className="e-c-gst">{money(e.gst)}</span>
                  <span className="e-c-amt">{money(e.amountIncGst)}</span>
                  <span className="e-c-status" style={{ color: STATUS_COLOUR[e.status] }}>
                    {STATUS_LABEL[e.status]}
                  </span>
                  {canManage && (
                    <span className="e-c-act">
                      {NEXT_STATUS[e.status] && (
                        <button className="btn sm" onClick={() => void advance(e)}>
                          {STATUS_LABEL[NEXT_STATUS[e.status] as ExpenseStatus]}
                        </button>
                      )}
                      <button className="btn sm" onClick={() => edit(e)}>Edit</button>
                    </span>
                  )}
                </div>
              ))}
              {expenses === null && <div className="pad label">Loading expenses…</div>}
              {expenses !== null && rows.length === 0 && (
                <div className="pad label">Nothing recorded for this period.</div>
              )}
            </div>
          </div>

          <div className="e-foot">
            <div>
              <div className="label">{totals.count} invoices · total incl GST</div>
              <div className="v">{money(totals.amountIncGst)}</div>
            </div>
            <div>
              <div className="label">GST claimable</div>
              <div className="v">{money(totals.gst)}</div>
            </div>
            <div>
              <div className="label">Awaiting a slip</div>
              <div className="v" style={{ color: totals.unmatched ? "var(--warn)" : "var(--pos)" }}>
                {totals.unmatched}
              </div>
            </div>
          </div>
        </div>

        {form && (
          <aside className="aside sheet">
            <div className="row" style={{ padding: "18px 20px 0", alignItems: "flex-end" }}>
              <div>
                <div className="label">{form.expenseId ? "Amend" : "New expense"}</div>
                <div style={{ fontSize: 20, fontWeight: 600, marginTop: 4 }}>
                  {form.expenseId ? form.ref || "This invoice" : "From the invoice"}
                </div>
              </div>
              <span className="spacer" />
              <button className="btn sm" onClick={() => setForm(null)}>Close</button>
            </div>

            <div className="scroll">
              <div className="e-form">
                <div className="field">
                  <span>Photo of the slip</span>
                  {form.slipS3Key ? (
                    <div className="row" style={{ gap: 8 }}>
                      <button className="btn sm" onClick={() => void viewSlip(form.slipS3Key!)}>View slip</button>
                      <label className="btn sm dashed" style={{ cursor: "pointer" }}>
                        Replace
                        <input type="file" accept="image/*,application/pdf" capture="environment" hidden onChange={(e) => e.target.files?.[0] && void attachSlip(e.target.files[0])} />
                      </label>
                    </div>
                  ) : (
                    <label className="btn dashed" style={{ cursor: "pointer", height: 56, justifyContent: "center" }}>
                      {slipBusy ?? "Photograph or choose the slip"}
                      <input type="file" accept="image/*,application/pdf" capture="environment" hidden disabled={Boolean(slipBusy)} onChange={(e) => e.target.files?.[0] && void attachSlip(e.target.files[0])} />
                    </label>
                  )}
                  {form.reading && (
                    <div className="e-gst-hint">
                      Read from the slip{form.reading.confidence !== null ? ` at ${form.reading.confidence}% confidence` : ""}:
                      {" "}{form.reading.supplierName ?? "no vendor"}{form.reading.total !== null ? ` · ${money(form.reading.total)}` : ""}{form.reading.gst !== null ? ` · GST ${money(form.reading.gst)}` : ""}{form.reading.ref ? ` · ${form.reading.ref}` : ""}.
                      {form.reading.lines.length > 0 ? ` ${form.reading.lines.length} line${form.reading.lines.length === 1 ? "" : "s"}.` : ""} Check each field before saving.
                    </div>
                  )}
                  {form.matches.length > 0 && (
                    <div className="e-matches">
                      <div className="label">Orders this slip could be paying for</div>
                      {form.matches.map((m) => {
                        const r = m.reconciliation;
                        const linked = form.orderId === m.orderId;
                        return (
                          <div key={m.orderId} className="e-match" aria-selected={linked}>
                            <div className="row" style={{ alignItems: "baseline" }}>
                              <span style={{ fontWeight: 600 }}>{m.supplierName}</span>
                              <span className="num muted" style={{ fontSize: 11 }}>
                                {money(m.subtotalIncGstCents / 100)} · {m.sentAt ? m.sentAt.slice(0, 10) : "—"} · {m.status}
                              </span>
                              <span className="spacer" />
                              <span className="num" style={{ fontSize: 12, color: m.score >= 70 ? "var(--pos)" : "var(--warn)" }}>{m.score}%</span>
                            </div>
                            <div className="e-gst-hint" style={{ marginTop: 4 }}>{m.reasons.slice(0, 2).join(". ")}.</div>
                            <div className="e-recon">
                              {r.matched.map((p) => (
                                <div
                                  key={p.order.name}
                                  className={"e-recon-line " + (p.delta === null || Math.abs(p.delta) < 0.01 ? "" : p.delta > 0 ? "neg" : "pos")}
                                >
                                  <span>{p.order.name}</span>
                                  <span className="num">{p.order.qty} × {money(p.order.unitCostCents / 100)} = {money(p.orderedAmount)}</span>
                                  <span className="num">{p.invoicedAmount === null ? "—" : money(p.invoicedAmount)}</span>
                                  <span className="num">{p.delta === null ? "" : p.delta === 0 ? "✓" : (p.delta > 0 ? "+" : "") + money(p.delta)}</span>
                                </div>
                              ))}
                              {r.unbilled.map((l) => (
                                <div key={"u" + l.name} className="e-recon-line warn">
                                  <span>{l.name}</span><span className="num">{l.qty} × {money(l.unitCostCents / 100)}</span><span className="num">not billed</span><span />
                                </div>
                              ))}
                              {r.unexpected.map((l) => (
                                <div key={"x" + l.name} className="e-recon-line warn">
                                  <span>{l.name}</span><span className="num">not ordered</span><span className="num">{l.amount === null ? "—" : money(l.amount)}</span><span />
                                </div>
                              ))}
                            </div>
                            <div className="row" style={{ marginTop: 8 }}>
                              <span className="label" style={{ letterSpacing: "0.08em" }}>
                                {r.deltaTotal === 0 ? "Lines agree ex GST" : "Lines differ by " + money(r.deltaTotal) + " ex GST"}
                              </span>
                              <span className="spacer" />
                              <button
                                type="button"
                                className="btn sm"
                                aria-pressed={linked}
                                onClick={() =>
                                  setForm((f) =>
                                    f
                                      ? {
                                          ...f,
                                          orderId: linked ? null : m.orderId,
                                          linkedSupplierId: linked ? null : m.supplierId,
                                          supplierName: linked ? f.supplierName : m.supplierName || f.supplierName,
                                        }
                                      : f,
                                  )
                                }
                              >
                                {linked ? "Linked · received on save" : "Link to this order"}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <label className="field">
                  <span>Supplier</span>
                  <input
                    type="text"
                    autoFocus
                    value={form.supplierName}
                    onChange={(e) => setForm({ ...form, supplierName: e.target.value })}
                  />
                </label>
                <div className="row-2">
                  <label className="field">
                    <span>Reference</span>
                    <input
                      type="text"
                      value={form.ref}
                      onChange={(e) => setForm({ ...form, ref: e.target.value })}
                    />
                  </label>
                  <label className="field">
                    <span>Invoice date</span>
                    <input
                      type="date"
                      value={form.date}
                      onChange={(e) => setForm({ ...form, date: e.target.value })}
                    />
                  </label>
                </div>
                <div className="row-2">
                  <label className="field">
                    <span>Amount incl GST</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    />
                  </label>
                  <label className="field">
                    <span>GST</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder={suggestedGst.toFixed(2)}
                      value={form.gst}
                      onChange={(e) => setForm({ ...form, gst: e.target.value })}
                    />
                  </label>
                </div>
                <div className="e-gst-hint">
                  Leave GST blank for one eleventh — {money(suggestedGst)}. Override it where part
                  of the invoice is GST-free, as most fresh produce is.
                </div>
                <label className="field">
                  <span>Status</span>
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value as ExpenseStatus })}
                  >
                    {EXPENSE_STATUSES.map((s) => (
                      <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span>Note</span>
                  <textarea
                    value={form.note}
                    maxLength={500}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                  />
                </label>
                <div className="e-gst-hint">
                  Slip photographs are not wired up yet, so nothing can be attached here.
                </div>
              </div>
            </div>

            <div className="foot">
              <button className="btn primary" onClick={() => setForm(null)}>Cancel</button>
              <button className="btn primary" disabled={busy} onClick={() => void save()}>
                {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
